import { formatDate, formatRub } from '@/landing/demoScenarios'
import { resolveAnalysisProgress } from '@/utils/analysisPipelineProgress'

const KANBAN_COLORS = {
  review: '#64748b',
  bidding: '#2563eb',
  documents: '#d97706',
}

const RELEVANCE_LABEL = {
  high: 'высокая',
  medium: 'средняя',
  low: 'низкая',
}

const RISK_LEVEL_LABEL = {
  low: 'низкий',
  medium: 'средний',
  high: 'высокий',
}

const SEVERITY_LABEL = {
  critical: 'критический',
  high: 'высокий',
  medium: 'средний',
  low: 'низкий',
}

function resolveTenderNumber(tender, index) {
  if (tender.number) return tender.number
  const base = `${tender.customer || ''}${tender.title || ''}${index}`.length
  const seq = String(710000000000000000 + base * 137 + index * 17).slice(0, 19)
  return seq
}

function resolveRegion(tender) {
  if (tender.region) return tender.region
  const c = tender.customer || ''
  if (c.includes('Казан')) return 'Республика Татарстан'
  if (c.includes('Москв')) return 'г. Москва'
  return 'Российская Федерация'
}

function resolveSelectionMethod(tender) {
  if (tender.selectionMethod) return tender.selectionMethod
  return tender.law === '44-ФЗ' ? 'Электронный аукцион' : 'Запрос котировок'
}

function resolveAuctionDate(tender) {
  if (tender.auctionDate) return tender.auctionDate
  const d = new Date(tender.deadline)
  if (Number.isNaN(d.getTime())) return null
  d.setDate(d.getDate() - 5)
  return d.toISOString().slice(0, 10)
}

function daysToDeadline(iso) {
  const deadline = new Date(iso)
  if (Number.isNaN(deadline.getTime())) return 0
  const diffMs = deadline.getTime() - Date.now()
  return Math.max(0, Math.ceil(diffMs / (24 * 60 * 60 * 1000)))
}

export function estimateMarginPercent(tender, index) {
  const base = tender.relevance === 'high' ? 22 : tender.relevance === 'medium' ? 15 : 8
  const riskPenalty = tender.riskLevel === 'high' ? 8 : tender.riskLevel === 'medium' ? 4 : 0
  const lawAdjust = tender.law === '44-ФЗ' ? -1 : 1
  const spreadAdjust = index % 2 === 0 ? 2 : -1
  return Math.max(4, Math.min(32, base - riskPenalty + lawAdjust + spreadAdjust))
}

export function estimateWinProbability(tender, daysLeft, marginPct) {
  let score = 45
  if (tender.relevance === 'high') score += 22
  else if (tender.relevance === 'medium') score += 11
  else score -= 10

  if (tender.riskLevel === 'high') score -= 18
  else if (tender.riskLevel === 'medium') score -= 7
  else score += 5

  if (daysLeft <= 10) score -= 10
  else if (daysLeft >= 20) score += 4

  score += Math.round((marginPct - 14) / 2)
  return Math.max(15, Math.min(92, score))
}

function resolveKanban(tender) {
  if (tender.kanbanStatusLabel) {
    return {
      label: tender.kanbanStatusLabel,
      color: tender.kanbanStatusColor || KANBAN_COLORS.review,
    }
  }
  if (tender.relevance === 'high') {
    return { label: 'На рассмотрении', color: KANBAN_COLORS.review }
  }
  if (tender.relevance === 'medium') {
    return { label: 'Проверка условий', color: KANBAN_COLORS.documents }
  }
  return { label: 'Первичный отбор', color: KANBAN_COLORS.bidding }
}

function buildCriteriaSummary(tender) {
  if (Array.isArray(tender.criteriaSummary) && tender.criteriaSummary.length) {
    return tender.criteriaSummary
  }
  const tagPart = (tender.tags || []).slice(0, 2).join(', ')
  const lines = []
  if (tagPart) lines.push(`Соответствие ТЗ: ${tagPart}`)
  lines.push(
    tender.law === '44-ФЗ'
      ? 'Цена контракта: не выше НМЦК'
      : 'Коммерческие условия: по запросу котировок',
  )
  if (tender.risk) lines.push(`Риск: ${tender.risk}`)
  return lines
}

function buildCommentText(tender, winProbability, marginPct) {
  if (tender.commentText) return tender.commentText
  if (tender.relevance === 'low' || tender.riskLevel === 'high') {
    return `Осторожно: ${tender.risk || 'повышенные риски'}. Оценочная маржа ${marginPct}%, вероятность победы ${winProbability}%.`
  }
  if (winProbability >= 70) {
    return `Приоритетная закупка: вероятность победы ${winProbability}%, маржа ${marginPct}%. ${tender.risk || 'Условия в целом приемлемы'}.`
  }
  return `Средний приоритет: вероятность победы ${winProbability}%. ${tender.risk || 'Проверить сроки и обеспечение заявки'}.`
}

function buildWinReasoning(tender, winProbability, marginPct, daysLeft) {
  if (tender.modal?.winReasoning) return tender.modal.winReasoning

  const rel = RELEVANCE_LABEL[tender.relevance] || 'не определена'
  const risk = RISK_LEVEL_LABEL[tender.riskLevel] || 'не определён'
  const tags = (tender.tags || []).join(', ') || 'предмет закупки'
  const lawNote =
    tender.law === '44-ФЗ'
      ? 'Закупка по 44-ФЗ: ценовой критерий доминирует, важно уложиться в НМЦК и подтвердить опыт по аналогам.'
      : 'Закупка по 223-ФЗ: больший вес коммерческих условий и квалификации поставщика.'

  const marginNote =
    marginPct >= 20
      ? `Смета по позициям даёт маржу около ${marginPct}% — запас на скидку при торгах есть.`
      : marginPct >= 12
        ? `Маржа около ${marginPct}% — участие возможно, но скидка на аукционе должна быть ограниченной.`
        : `Маржа около ${marginPct}% — ниже целевого порога, целесообразно пересчитать себестоимость или отложить подачу.`

  const timingNote =
    daysLeft <= 10
      ? `До окончания подачи осталось ${daysLeft} дн. — потребуется ускоренная подготовка комплекта документов.`
      : daysLeft <= 20
        ? `До дедлайна ${daysLeft} дн. — срок достаточный при готовых шаблонах заявки и согласовании цены.`
        : `До окончания подачи ${daysLeft} дн. — календарь комфортный для согласования внутри компании.`

  const riskNote = tender.risk
    ? `Ключевой риск по документации: ${tender.risk}. Рекомендуется заложить его в дорожную карту согласования.`
    : 'Существенных блокирующих рисков в демо-скане не выявлено.'

  const historyNote =
    winProbability >= 70
      ? 'По профилю компании и истории выигранных закупок за последние месяцы заказчик и предмет закупки выглядят близкими к вашей специализации.'
      : winProbability >= 50
        ? 'Есть пересечение с прошлыми победами, но конкуренция или условия ТЗ могут снизить итоговый балл.'
        : 'Совпадение с выигранными тендерами слабое — имеет смысл сравнить требования с реальными кейсами перед подачей.'

  const recommendation =
    winProbability >= 70
      ? 'Рекомендация: включить закупку в приоритетный список и подготовить заявку в первую очередь.'
      : winProbability >= 50
        ? 'Рекомендация: подать заявку после уточнения маржи и закрытия рисков из антириск-скана.'
        : 'Рекомендация: не подаваться без пересмотра цены, состава работ или исключения обременительных условий.'

  return [
    `Итоговая оценка вероятности победы — ${winProbability}%.`,
    '',
    `1. Соответствие запросу: ${rel} релевантность по ключевым словам и предмету («${tags}»). ${lawNote}`,
    '',
    `2. Экономика: ${marginNote}`,
    '',
    `3. Сроки: ${timingNote}`,
    '',
    `4. Риски (${risk} уровень по демо-скану): ${riskNote}`,
    '',
    `5. История побед: ${historyNote}`,
    '',
    recommendation,
  ].join('\n')
}

function buildCriteriaRows(tender) {
  if (Array.isArray(tender.modal?.criteria)) return tender.modal.criteria
  const rows = [
    {
      number: '1',
      level: 0,
      name: 'Цена контракта',
      weight: '60',
      description: 'Предложение не выше начальной (максимальной) цены',
    },
    {
      number: '1.1',
      level: 1,
      name: 'Снижение НМЦК',
      weight: '—',
      description: 'Оценка конкурентности ценового предложения',
    },
    {
      number: '2',
      level: 0,
      name: 'Квалификация участника',
      weight: '40',
      description: tender.risk || 'Требования из документации закупки',
    },
  ]
  if ((tender.tags || []).length) {
    rows.push({
      number: '2.1',
      level: 1,
      name: 'Соответствие предмету закупки',
      weight: '—',
      description: (tender.tags || []).join('; '),
    })
  }
  return rows
}

function buildProductItems(tender, index) {
  if (Array.isArray(tender.modal?.products)) return tender.modal.products
  const tag = (tender.tags || [])[0] || 'Позиция'
  const unitPrice = Math.round((tender.price || 0) / (3 + (index % 2)))
  return [
    {
      name: `${tag} (основная)`,
      description: 'По спецификации из ТЗ',
      quantity: 1 + (index % 3),
      unit: 'компл.',
      price_from_docs: unitPrice,
    },
    {
      name: `${tag} (дополнительно)`,
      description: 'Сопутствующие материалы и расходники',
      quantity: 2 + index,
      unit: 'шт.',
      price_from_docs: Math.round(unitPrice * 0.35),
    },
    {
      name: 'Монтаж / ввод в эксплуатацию',
      description: tender.riskLevel === 'high' ? 'Обязательный этап по 44-ФЗ' : 'По усмотрению заказчика в ТЗ',
      quantity: 1,
      unit: 'усл.',
      price_from_docs: Math.round(unitPrice * 0.15),
    },
  ]
}

function buildCompliance(tender, marginPct) {
  if (tender.modal?.compliance) return tender.modal.compliance
  const lawBasis = tender.law === '44-ФЗ' ? '44' : '223'
  const riskScore =
    tender.riskLevel === 'high' ? 68 : tender.riskLevel === 'medium' ? 42 : 24
  const sev1 = tender.riskLevel === 'high' ? 'high' : 'medium'
  const findings = [
    {
      id: 1,
      severity: sev1,
      severityLabel: SEVERITY_LABEL[sev1],
      title: tender.risk || 'Проверьте сроки исполнения',
      description: 'Сроки подачи и исполнения требуют подтверждения ресурсами.',
      recommendation: 'Сверить календарь с производственным планом.',
      law_ref: tender.law === '44-ФЗ' ? 'ст. 34 44-ФЗ' : '223-ФЗ, положение о закупке',
      review_status: 'open',
    },
  ]
  if (marginPct < 12) {
    findings.push({
      id: 2,
      severity: 'critical',
      severityLabel: SEVERITY_LABEL.critical,
      title: 'Низкая маржинальность по смете',
      description: `Оценочная маржа ${marginPct}% ниже целевого порога.`,
      recommendation: 'Пересчитать себестоимость или отказаться от подачи.',
      law_ref: 'Коммерческий риск',
      review_status: 'open',
    })
  } else {
    findings.push({
      id: 2,
      severity: 'low',
      severityLabel: SEVERITY_LABEL.low,
      title: 'Документация в целом полная',
      description: 'Комплект вложений достаточен для первичного анализа.',
      recommendation: 'Уточнить детали в чате с искусственным интеллектом при необходимости.',
      law_ref: '—',
      review_status: 'accepted',
    })
  }
  return {
    scan: {
      status: 'done',
      risk_score: riskScore,
      law_basis: lawBasis,
      summary: `Демонстрационный скан: ${findings.length} замечаний, суммарный балл риска ${riskScore}.`,
      roadmap_tasks_added: tender.riskLevel === 'high' ? 2 : 0,
    },
    findings,
  }
}

function buildRoadmapTasks(tender, index) {
  if (Array.isArray(tender.modal?.roadmap)) return tender.modal.roadmap
  const base = new Date(tender.deadline || '2026-07-01')
  const overdue = new Date(base)
  overdue.setDate(overdue.getDate() - 14)
  const soon = new Date(base)
  soon.setDate(soon.getDate() - 7)
  return [
    {
      id: 1,
      title: 'Проверить комплект заявки',
      description: 'Сверить перечень документов с ТЗ',
      due_at: soon.toISOString().slice(0, 10),
      status: index === 1 && tender.riskLevel === 'high' ? 'todo' : 'done',
    },
    {
      id: 2,
      title: `Проверить: ${tender.risk || 'условия заказчика'}`,
      description: 'Задача из антириск-скана (демо)',
      due_at: overdue.toISOString().slice(0, 10),
      status: 'todo',
    },
    {
      id: 3,
      title: 'Согласовать цену с руководством',
      due_at: base.toISOString().slice(0, 10),
      status: 'todo',
    },
  ]
}

function buildChatMessages(tender) {
  if (Array.isArray(tender.modal?.chat)) return tender.modal.chat
  return [
    {
      id: 'u1',
      role: 'user',
      content: 'Какие документы обязательны для подачи заявки?',
      created_at: '2026-05-20T10:00:00Z',
    },
    {
      id: 'a1',
      role: 'assistant',
      content:
        `По демо-анализу «${tender.title}»: декларация соответствия, выписка ЕГРЮЛ, опыт аналогичных контрактов (если указан в ТЗ), спецификация по ${(tender.tags || ['позициям']).join(', ')}.`,
      created_at: '2026-05-20T10:01:00Z',
    },
  ]
}

function buildOverviewEntries(tender, index) {
  if (Array.isArray(tender.modal?.overviewEntries)) return tender.modal.overviewEntries
  return [
    { label: 'Номер закупки', value: `№ ${resolveTenderNumber(tender, index)}` },
    { label: 'Закон', value: tender.law },
    { label: 'Заказчик', value: tender.customer },
    { label: 'Регион', value: resolveRegion(tender) },
    { label: 'Способ отбора', value: resolveSelectionMethod(tender) },
    { label: 'Начальная цена', value: formatRub(tender.price) },
    { label: 'Окончание подачи', value: formatDate(tender.deadline) },
    {
      label: 'Площадка',
      value:
        tender.law === '44-ФЗ'
          ? 'Единая информационная система (zakupki.gov.ru)'
          : 'Электронная торговая площадка и ЕИС',
    },
  ]
}

function buildAnalysisRow(tender, index, scenarioId) {
  const id = `demo-${scenarioId}-${index}`
  const daysLeft = daysToDeadline(tender.deadline)
  const marginPct = estimateMarginPercent(tender, index)
  const marginReady = tender.relevance !== 'low'
  const winProbability = estimateWinProbability(tender, daysLeft, marginPct)
  const kanban = resolveKanban(tender)
  const hasDocuments = tender.hasDocuments !== false && tender.relevance !== 'low'

  const syntheticRow = {
    id,
    progress: 'analyzed',
    pipeline_complete: true,
    pipeline_running: false,
    win_probability_percent: winProbability,
  }

  const analysisProgress = resolveAnalysisProgress(syntheticRow, null)

  return {
    id,
    tenderNumber: resolveTenderNumber(tender, index),
    title: tender.title,
    customer: tender.customer,
    region: resolveRegion(tender),
    selectionMethod: resolveSelectionMethod(tender),
    budget: tender.price,
    deadline: tender.deadline,
    auctionDate: resolveAuctionDate(tender),
    marginValue: marginReady ? marginPct : null,
    marginText: marginReady ? `${Math.round(marginPct)}%` : '—',
    marginIsGood: marginReady && marginPct > 20,
    marginIsPositive: marginPct >= 0,
    criteriaSummary: buildCriteriaSummary(tender),
    commentText: buildCommentText(tender, winProbability, marginPct),
    probability: winProbability,
    progress: 'analyzed',
    pipeline_complete: true,
    analysisProgress,
    kanbanStatusLabel: kanban.label,
    kanbanStatusColor: kanban.color,
    hasDocuments,
    law: tender.law,
    relevance: tender.relevance,
    riskLevel: tender.riskLevel,
    _demoTender: tender,
    _demoIndex: index,
  }
}

export function mapDemoTenderToRow(tender, index, scenarioId) {
  return buildAnalysisRow(tender, index, scenarioId)
}

export function mapDemoTenderToModalPayload(row) {
  const tender = row._demoTender || {}
  const index = row._demoIndex ?? 0
  const winProbability = row.probability
  const marginPct = row.marginValue ?? estimateMarginPercent(tender, index)
  const daysLeft = daysToDeadline(tender.deadline)
  const compliance = buildCompliance(tender, marginPct)

  const analysis = {
    progress: 'analyzed',
    pipeline_complete: true,
    pipeline_running: false,
    win_probability_percent: winProbability,
    win_short_comment: buildCommentText(tender, winProbability, marginPct),
    win_reasoning: buildWinReasoning(tender, winProbability, marginPct, daysLeft),
    analysis_date: '2026-05-20',
  }

  return {
    row,
    analysis,
    overviewEntries: buildOverviewEntries(tender, index),
    attachments: tender.modal?.attachments || [
      { name: 'Извещение о закупке.pdf' },
      { name: 'Техническое задание.docx' },
      { name: 'Проект контракта.docx' },
    ],
    criteriaRows: buildCriteriaRows(tender),
    products: buildProductItems(tender, index),
    compliance,
    roadmapTasks: buildRoadmapTasks(tender, index),
    chatMessages: buildChatMessages(tender),
    analysisProgress: row.analysisProgress,
  }
}

export function mapScenarioToRows(scenario) {
  if (!scenario?.tenders) return []
  return scenario.tenders.map((t, idx) => mapDemoTenderToRow(t, idx, scenario.id))
}
